import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { Forbidden } from '../../../../../components/error/Forbidden';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateDiscountMutation,
  useGetDiscountQuery,
  useUpdateDiscountsMutation
} from '../../../../../services/endpoints/orders/discounts';
import { getCurrentUser } from '../../../../../services/helper';
import { Discount } from '../../../../../types/orders.type';
import { scholarshipTypes } from '../../../../../utils/consts/orders/scholarships';
import { getErrorMessage, getObjectDifference } from '../../../../../utils/utils';
import { isChildScopedDiscount } from '../../../../../utils/orders/discountApplication';
import { FadeIn } from '../../../../animations/FadeIn';
import { DiscountForm } from './DiscountForm';
import { userCanAdmin_Discounts } from './DiscountsView';

const steps = [{ name: 'Details', form: DiscountForm }];

export interface FormValues {
  user_id: { value?: number; label: string };
  scholarship_id: { value?: number; label: string };
  course_id: { value?: number; label: string };
  child_id?: { value?: number; label: string } | null;
}

type Overwrite<T, NewT> = Omit<T, keyof NewT> & NewT;

export type CreateFormValues = Overwrite<
  FormValues,
  {
    user_id: number;
    scholarship_id: number;
    course_id: number;
    child_id?: number;
  }
>;

const toDiscountPayload = (formValue: FormValues): CreateFormValues => {
  const childId = formValue.child_id?.value;
  return {
    user_id: formValue.user_id?.value ?? 0,
    scholarship_id: formValue.scholarship_id?.value ?? 0,
    course_id: formValue.course_id?.value ?? 0,
    ...(childId != null && childId > 0 ? { child_id: childId } : {})
  };
};

const toDiscountFields = (discount: Discount): CreateFormValues => ({
  user_id: discount.user_id,
  scholarship_id: discount.scholarship_id,
  course_id: discount.course_id,
  ...(isChildScopedDiscount(discount) ? { child_id: discount.child_id! } : {})
});

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditDiscountProps {
  editing?: boolean;
}

export const AddEditDiscount: React.FC<AddEditDiscountProps> = ({ editing }) => {
  const params = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const [createDiscount] = useCreateDiscountMutation();
  const [updateDiscount] = useUpdateDiscountsMutation();
  const { data, error, isLoading } = useGetDiscountQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    user_id: { label: '' },
    scholarship_id: { label: '' },
    course_id: { label: '' }
  });

  const validationSchemas = [
    Yup.object({
      user_id: Yup.object({
        value: Yup.number().required('User selection is required'),
        label: Yup.string().optional()
      }).required(),
      scholarship_id: Yup.object({
        value: Yup.number().required('Scholarship selection is required'),
        label: Yup.string().optional()
      }).required(),
      course_id: Yup.object({
        value: Yup.number().required('Course selection is required'),
        label: Yup.string().optional()
      }).required(),
      child_id: Yup.object({
        value: Yup.number().optional().nullable(),
        label: Yup.string().optional()
      })
        .optional()
        .nullable()
    })
  ];

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading discount', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data?.discount) {
      const { discount } = data;
      setInitialValues({
        user_id: {
          value: discount.user_id,
          label: discount.user
            ? `${discount.user.first_name} ${discount.user.last_name} (${discount.user.email})`
            : ''
        },
        scholarship_id: {
          value: discount.scholarship_id,
          label: `${scholarshipTypes[discount.scholarship.scholarship_type]} (${discount.scholarship.percent_discount}% off)`
        },
        course_id: { value: discount.course_id ?? 0, label: discount.course?.title ?? '' },
        child_id: discount.child_id
          ? {
              value: discount.child_id,
              label:
                discount.child?.full_name ??
                `${discount.child?.first_name ?? ''} ${discount.child?.last_name ?? ''}`.trim()
            }
          : undefined
      });
    }
  }, [data]);

  const canAdminDiscounts = userCanAdmin_Discounts(currentUser);

  const handleSumbit = async (formValue: FormValues) => {
    try {
      const submission = toDiscountPayload(formValue);

      if (editing) {
        if (!data?.discount) {
          throw new Error('Discount not loaded');
        }
        const update = getObjectDifference(toDiscountFields(data.discount), submission);
        if (Object.keys(update).length > 0) {
          const res = await updateDiscount({
            id: data.discount.id,
            data: submission
          }).unwrap();
          if (res?.discount) {
            addToast('Discount updated succesfully', 'success');
          } else {
            addToast('No changes to discount', 'info');
          }
          navigate(`/admin/orders/discounts/${data.discount.id}`);
        } else {
          addToast('No changes to discount', 'info');
          navigate(`/admin/orders/discounts/${data.discount.id}`);
        }
      } else {
        const res = await createDiscount(submission).unwrap();
        if (res?.discount) {
          navigate(`/admin/orders/discounts/${res.discount.id}`);
          addToast('Discount created succesfully', 'success');
        } else throw new Error('A problem happened while creating discount');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        user_id: { value: true },
        scholarship_id: { value: true },
        course_id: { value: true }
      });
    }
  };

  if (!canAdminDiscounts) {
    return <Forbidden />;
  }

  if (editing && isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to discount${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing ? `/admin/orders/discounts/${data?.discount?.id}` : '/admin/orders/discounts'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Discount
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleSumbit}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
